import type { Feed } from '@knocklabs/client'
import { useIsLg } from '@seen/ui'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSearchParams } from 'react-router'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { archiveInboxItemsVerified } from '#src/hooks/inbox/inboxLiveRemoval.ts'
import type { InboxListItem } from '#src/hooks/inbox/useInboxDedupedItems.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'

export interface UseInboxSidebarOptions {
  /** The deduplicated, filtered list of items to navigate. */
  items: InboxListItem[]
  /** Sibling resolver from useInboxDedupedItems — used for auto-mark-read and hotkeys. */
  getSiblings: (item: NotificationItem) => NotificationItem[]
  /* The single group-key definition, shared with the list. Must be the same
     resolver instance the deduped list used, or the two disagree. */
  resolveGroupKey: (item: NotificationItem) => string | null
  /**
   * Optional: current category value. When provided, selection is cleared on
   * category change (but NOT on initial mount, so back-navigation works).
   */
  selectedCategory?: string
  /** Feed client for mutations (mark read, archive, etc.). */
  feedClient: Feed
}

export interface UseInboxSidebarResult {
  /** Call to update the ?selected= URL param. Pass undefined to clear. */
  selectItem: (key: string | undefined) => void
  /** Raw ?selected= value from URL params. */
  selectedItemKey: string | null
  /**
   * The item to show in the sidebar. Bridges the gap between the URL param
   * updating and the item disappearing from the list (e.g. after archiving).
   */
  effectiveSelectedItem: InboxListItem | undefined
  /**
   * Key to use for row highlighting — follows the effective item even before
   * the URL param catches up.
   */
  effectiveKey: string | null | undefined
  /** True when the sidebar should be shown (desktop + item selected). */
  sidebarOpen: boolean
  /**
   * Advance ?selected= to the neighbor if `key` is the current selection;
   * no-op otherwise. Row-level archive paths (hover ✓, context menu) call
   * this BEFORE firing the mutation so the URL doesn't end up pointing at a
   * row that's about to disappear from the list — keeps j/k findIndex valid
   * and prevents the sidebar from flashing as the list shrinks under it.
   * The page-level E hotkey has equivalent inline logic below.
   */
  advanceSelectionAwayFrom: (key: string) => void
  /** Archive items now, deferring those that are the open selection (pin). */
  archiveOrDefer: (items: NotificationItem[]) => void
}

/*
 * Encapsulates all sidebar/selection logic shared between InboxPage and
 * InboxDonePage: URL-param selection, neighbor-fallback on item disappearance,
 * auto-mark-read, category-change clearing, and keyboard navigation.
 */
export function useInboxSidebar({
  items,
  getSiblings,
  resolveGroupKey,
  selectedCategory,
  feedClient,
}: UseInboxSidebarOptions): UseInboxSidebarResult {
  const isDesktop = useIsLg()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedItemKey = searchParams.get('selected')

  const selectItem = useCallback(
    (key: string | undefined) => {
      setSearchParams((prev) => {
        if (key) {
          prev.set('selected', key)
        } else {
          prev.delete('selected')
        }
        return prev
      })
    },
    [setSearchParams]
  )

  /*
   * Cached selection — three-tier resolution that keeps the sidebar stable
   * across Knock store mutations (auto-archive on task completion, sibling
   * archives, websocket-driven rep flips):
   *
   *   Tier 1 — `liveExact`: items.find by selectedItemKey. The common case.
   *   Tier 2 — `liveSameGroup`: same group, different rep. Happens when a
   *            newer sibling arrives via socket (becomes the new rep) or
   *            when a single sibling is archived. Silently re-points the URL
   *            to the new rep so future renders hit Tier 1.
   *   Tier 3 — `cached.item` snapshot: the group is fully gone (whole-group
   *            archive). Keep showing the last-known item until the user
   *            navigates explicitly — they don't lose their place mid-edit.
   *
   * `InboxListItem.key` is the representative notif's Knock ID, NOT the
   * group key, so we must cache by `resolveGroupKey(item)` to survive
   * rep flips. See `useInboxGroupKeyResolver`.
   */
  const [cached, setCached] = useState<
    { groupKey: string | null; item: InboxListItem } | undefined
  >()

  /* Latest feedClient + archives deferred because their item is the open
     selection (pin). Flushed once the selection moves off the item's group. */
  const feedClientRef = useRef(feedClient)
  feedClientRef.current = feedClient
  const pendingArchivesRef = useRef<Map<string, NotificationItem>>(new Map())

  const liveExact = useMemo(
    () => items.find((li) => li.key === selectedItemKey),
    [items, selectedItemKey]
  )

  const liveSameGroup = useMemo(() => {
    if (liveExact || !cached || cached.groupKey === null) {
      return undefined
    }
    return items.find((li) => {
      const gk = resolveGroupKey(li.item)
      return gk !== null && gk === cached.groupKey
    })
  }, [items, liveExact, cached, resolveGroupKey])

  /* Refresh cache whenever we have a live resolution. Always overwrite the
     cached groupKey to reflect the current selection — including writing null
     when resolveGroupKey returns null — so the previous selection's groupKey
     never leaks into Tier 2 fallback for a different item. */
  useEffect(() => {
    const live = liveExact ?? liveSameGroup
    if (live) {
      const gk = resolveGroupKey(live.item)
      setCached({ groupKey: gk, item: live })
    }
  }, [liveExact, liveSameGroup, resolveGroupKey])

  /* Clear cache when user explicitly dismisses (Escape, category change,
     onClose, etc.) so the cached snapshot doesn't outlive its welcome. */
  useEffect(() => {
    if (!selectedItemKey) {
      setCached(undefined)
    }
  }, [selectedItemKey])

  const effectiveSelectedItem = useMemo(() => {
    if (!selectedItemKey || !isDesktop) {
      return undefined
    }
    return liveExact ?? liveSameGroup ?? cached?.item
  }, [selectedItemKey, isDesktop, liveExact, liveSameGroup, cached])

  const openGroupKey = effectiveSelectedItem
    ? resolveGroupKey(effectiveSelectedItem.item)
    : null
  const openGroupKeyRef = useRef(openGroupKey)
  openGroupKeyRef.current = openGroupKey

  /* Archive now, except items whose group is the open selection — defer those
     (pin) until selection moves off the group. Reads the LIVE open-group via a
     ref so an item completed just as selection moves still classifies correctly. */
  const archiveOrDefer = useCallback(
    (toArchive: NotificationItem[]) => {
      const openGk = openGroupKeyRef.current
      const immediate: NotificationItem[] = []
      for (const it of toArchive) {
        const gk = resolveGroupKey(it)
        if (openGk !== null && gk !== null && gk === openGk) {
          pendingArchivesRef.current.set(it.id, it)
        } else {
          immediate.push(it)
        }
      }
      if (immediate.length > 0) {
        void archiveInboxItemsVerified(feedClientRef.current, immediate)
      }
    },
    [resolveGroupKey]
  )

  /* Flush deferred archives whose group is no longer the open selection. */
  useEffect(() => {
    const pending = pendingArchivesRef.current
    if (pending.size === 0) {
      return
    }
    const toFlush: NotificationItem[] = []
    for (const [id, item] of pending) {
      const gk = resolveGroupKey(item)
      if (gk === null || gk !== openGroupKey) {
        toFlush.push(item)
        pending.delete(id)
      }
    }
    if (toFlush.length > 0) {
      void archiveInboxItemsVerified(feedClientRef.current, toFlush)
    }
  }, [openGroupKey, resolveGroupKey])

  /* Flush anything still pending if the inbox unmounts (route change away). */
  useEffect(() => {
    return () => {
      const pending = pendingArchivesRef.current
      if (pending.size > 0) {
        void archiveInboxItemsVerified(feedClientRef.current, [
          ...pending.values(),
        ])
        pending.clear()
      }
    }
  }, [])

  /* Tier 2 silently re-points `?selected=` to the new rep so subsequent
     renders hit Tier 1. Tier 3 (cached-only) does NOT change the URL —
     we let the cached snapshot ride until the user navigates explicitly.
     Guard on selectedItemKey: an explicit dismiss (X / Escape clears the
     param to null) must not be rescued by a still-populated cache, or the
     sidebar reopens the instant it closes. */
  useLayoutEffect(() => {
    if (!isDesktop || !selectedItemKey) {
      return
    }
    if (!liveExact && liveSameGroup && liveSameGroup.key !== selectedItemKey) {
      selectItem(liveSameGroup.key)
    }
  }, [isDesktop, liveExact, liveSameGroup, selectedItemKey, selectItem])

  /* Auto-mark unread item (and its dedup siblings) as read when navigated to */
  const effectiveNotificationItem = effectiveSelectedItem?.item
  useEffect(() => {
    if (!effectiveNotificationItem || effectiveNotificationItem.read_at) {
      return
    }
    const siblings = getSiblings(effectiveNotificationItem)
    if (siblings.length > 0) {
      const unread = siblings.filter((s) => !s.read_at)
      if (unread.length > 0) {
        void feedClient.markAsRead(unread)
      }
    } else {
      void feedClient.markAsRead(effectiveNotificationItem)
    }
  }, [effectiveNotificationItem, feedClient, getSiblings])

  /* Clear sidebar selection when switching categories (but not on initial
     mount, so that back-navigation restores the ?selected= param from URL) */
  const prevCategoryRef = useRef(selectedCategory)
  useEffect(() => {
    const prev = prevCategoryRef.current
    prevCategoryRef.current = selectedCategory
    if (selectedCategory !== undefined && prev !== selectedCategory) {
      selectItem(undefined)
    }
  }, [selectedCategory, selectItem])

  /* Keyboard navigation */
  useHotkeys(
    'j,ArrowDown',
    (e) => {
      if (!isDesktop) {
        return
      }
      e.preventDefault()
      const currentIndex = items.findIndex((li) => li.key === selectedItemKey)
      const nextIndex = currentIndex + 1
      if (nextIndex < items.length) {
        selectItem(items[nextIndex]?.key)
      }
    },
    [items, selectedItemKey, selectItem, isDesktop]
  )

  useHotkeys(
    'k,ArrowUp',
    (e) => {
      if (!isDesktop) {
        return
      }
      e.preventDefault()
      const currentIndex = items.findIndex((li) => li.key === selectedItemKey)
      const prevIndex = currentIndex - 1
      if (prevIndex >= 0) {
        selectItem(items[prevIndex]?.key)
      } else if (currentIndex === -1 && items.length > 0) {
        selectItem(items[0]?.key)
      }
    },
    [items, selectedItemKey, selectItem, isDesktop]
  )

  useHotkeys(
    'Escape',
    () => {
      if (!isDesktop) {
        return
      }
      selectItem(undefined)
    },
    [selectItem, isDesktop]
  )

  const sidebarOpen = isDesktop && !!effectiveSelectedItem

  const advanceSelectionAwayFrom = useCallback(
    (key: string) => {
      if (key !== selectedItemKey) {
        return
      }
      const idx = items.findIndex((li) => li.key === key)
      const nextKey =
        idx !== -1 ? (items[idx + 1]?.key ?? items[idx - 1]?.key) : undefined
      selectItem(nextKey)
    },
    [items, selectedItemKey, selectItem]
  )

  /* Page-level archive/unarchive targeting the sidebar item. Advances
     selection to the neighbor BEFORE archiving so the cache moves to the
     new rep instead of holding the just-archived snapshot. */
  useHotkeys(
    Hotkeys.ToggleInboxDone,
    () => {
      if (!effectiveSelectedItem) {
        return
      }
      const siblings = getSiblings(effectiveSelectedItem.item)
      const allItems =
        siblings.length > 0 ? siblings : [effectiveSelectedItem.item]
      /* Advance to the neighbor BEFORE toggling — the item leaves the current
         list whether we archive (inbox) or unarchive (Done), so the cache must
         move to the new rep instead of holding the just-toggled snapshot. */
      const idx = items.findIndex((li) => li.key === effectiveSelectedItem.key)
      const nextKey =
        idx !== -1 ? (items[idx + 1]?.key ?? items[idx - 1]?.key) : undefined
      if (!effectiveSelectedItem.item.archived_at) {
        const unarchived = allItems.filter((s) => !s.archived_at)
        if (unarchived.length > 0) {
          selectItem(nextKey)
          void archiveInboxItemsVerified(feedClient, unarchived)
        }
      } else {
        const archived = allItems.filter((s) => !!s.archived_at)
        if (archived.length > 0) {
          selectItem(nextKey)
          void feedClient.markAsUnarchived(archived)
        }
      }
    },
    { enabled: sidebarOpen },
    [
      effectiveSelectedItem,
      feedClient,
      getSiblings,
      items,
      selectItem,
      sidebarOpen,
    ]
  )

  /* Page-level read/unread targeting the sidebar item */
  useHotkeys(
    Hotkeys.ToggleInboxRead,
    () => {
      if (!effectiveSelectedItem) {
        return
      }
      const siblings = getSiblings(effectiveSelectedItem.item)
      const allItems =
        siblings.length > 0 ? siblings : [effectiveSelectedItem.item]
      if (!effectiveSelectedItem.item.read_at) {
        const unread = allItems.filter((s) => !s.read_at)
        if (unread.length > 0) {
          void feedClient.markAsRead(unread)
        }
      } else {
        const read = allItems.filter((s) => !!s.read_at)
        if (read.length > 0) {
          void feedClient.markAsUnread(read)
        }
        selectItem(undefined)
      }
    },
    { enabled: sidebarOpen },
    [effectiveSelectedItem, feedClient, getSiblings, selectItem, sidebarOpen]
  )

  /* Use the effective item's key for row highlighting so the highlight
     follows the neighbor immediately, even before the URL param updates. */
  const effectiveKey = effectiveSelectedItem?.key ?? selectedItemKey

  return {
    selectItem,
    selectedItemKey,
    effectiveSelectedItem,
    effectiveKey,
    sidebarOpen,
    advanceSelectionAwayFrom,
    archiveOrDefer,
  }
}
